/**
 * Timezone utilities for displaying dates in user's preferred timezone
 * Logs are stored in UTC, this only affects display
 */

// Common timezones with display names
export const TIMEZONES = [
  { value: "UTC", label: "UTC (Coordinated Universal Time)" },
  { value: "America/New_York", label: "Eastern Time (ET)" },
  { value: "America/Chicago", label: "Central Time (CT)" },
  { value: "America/Denver", label: "Mountain Time (MT)" },
  { value: "America/Los_Angeles", label: "Pacific Time (PT)" },
  { value: "America/Anchorage", label: "Alaska Time (AKT)" },
  { value: "Pacific/Honolulu", label: "Hawaii Time (HST)" },
  { value: "America/Toronto", label: "Toronto (ET)" },
  { value: "America/Vancouver", label: "Vancouver (PT)" },
  { value: "Europe/London", label: "London (GMT/BST)" },
  { value: "Europe/Paris", label: "Paris (CET/CEST)" },
  { value: "Europe/Berlin", label: "Berlin (CET/CEST)" },
  { value: "Europe/Amsterdam", label: "Amsterdam (CET/CEST)" },
  { value: "Europe/Rome", label: "Rome (CET/CEST)" },
  { value: "Europe/Madrid", label: "Madrid (CET/CEST)" },
  { value: "Europe/Zurich", label: "Zurich (CET/CEST)" },
  { value: "Europe/Stockholm", label: "Stockholm (CET/CEST)" },
  { value: "Europe/Moscow", label: "Moscow (MSK)" },
  { value: "Asia/Dubai", label: "Dubai (GST)" },
  { value: "Asia/Kolkata", label: "India (IST)" },
  { value: "Asia/Singapore", label: "Singapore (SGT)" },
  { value: "Asia/Hong_Kong", label: "Hong Kong (HKT)" },
  { value: "Asia/Shanghai", label: "Shanghai (CST)" },
  { value: "Asia/Tokyo", label: "Tokyo (JST)" },
  { value: "Asia/Seoul", label: "Seoul (KST)" },
  { value: "Australia/Sydney", label: "Sydney (AEST/AEDT)" },
  { value: "Australia/Melbourne", label: "Melbourne (AEST/AEDT)" },
  { value: "Australia/Perth", label: "Perth (AWST)" },
  { value: "Pacific/Auckland", label: "Auckland (NZST/NZDT)" },
  { value: "America/Sao_Paulo", label: "São Paulo (BRT)" },
  { value: "America/Mexico_City", label: "Mexico City (CST)" },
  { value: "Africa/Johannesburg", label: "Johannesburg (SAST)" },
  { value: "Africa/Cairo", label: "Cairo (EET)" },
];

/**
 * Format a date/time string in the user's preferred timezone
 * @param {string} dateString - ISO date string (UTC)
 * @param {string} timezone - IANA timezone string (e.g., "America/New_York")
 * @param {object} options - Intl.DateTimeFormat options
 * @returns {string} Formatted date string in user's timezone
 */
export const formatInTimezone = (dateString, timezone = "UTC", options = {}) => {
  if (!dateString) return "Unknown";
  
  try {
    const date = new Date(dateString);
    
    const defaultOptions = {
      timeZone: timezone,
      year: "numeric",
      month: "numeric",
      day: "numeric",
      hour: "numeric",
      minute: "numeric",
      second: "numeric",
      hour12: true,
      ...options
    };
    
    return new Intl.DateTimeFormat("en-US", defaultOptions).format(date);
  } catch (error) {
    console.error("Timezone format error:", error);
    // Fallback to local time
    return new Date(dateString).toLocaleString();
  }
};

/**
 * Format a date/time for display with timezone abbreviation
 * @param {string} dateString - ISO date string (UTC)
 * @param {string} timezone - IANA timezone string
 * @returns {string} Formatted date with timezone abbreviation
 */
export const formatWithTzAbbrev = (dateString, timezone = "UTC") => {
  if (!dateString) return "Unknown";
  
  try {
    const date = new Date(dateString);
    
    // Get formatted date
    const formatted = new Intl.DateTimeFormat("en-US", {
      timeZone: timezone,
      month: "short",
      day: "numeric",
      hour: "numeric",
      minute: "2-digit",
      hour12: true
    }).format(date);
    
    // Get timezone abbreviation
    const tzAbbrev = new Intl.DateTimeFormat("en-US", {
      timeZone: timezone,
      timeZoneName: "short"
    }).formatToParts(date).find(part => part.type === "timeZoneName")?.value || "";
    
    return `${formatted} ${tzAbbrev}`;
  } catch (error) {
    console.error("Timezone format error:", error);
    return new Date(dateString).toLocaleString();
  }
};

/**
 * Get short format for compact displays
 * @param {string} dateString - ISO date string (UTC)
 * @param {string} timezone - IANA timezone string
 * @returns {string} Short formatted date
 */
export const formatShort = (dateString, timezone = "UTC") => {
  if (!dateString) return "";
  
  try {
    const date = new Date(dateString);
    
    return new Intl.DateTimeFormat("en-US", {
      timeZone: timezone,
      month: "numeric",
      day: "numeric",
      hour: "numeric",
      minute: "2-digit",
      hour12: true
    }).format(date);
  } catch (error) {
    return new Date(dateString).toLocaleString();
  }
};

/**
 * Get relative time (e.g., "2 hours ago") with timezone context
 * @param {string} dateString - ISO date string (UTC)
 * @returns {string} Relative time string
 */
export const formatRelative = (dateString) => {
  if (!dateString) return "";
  
  try {
    const date = new Date(dateString);
    const now = new Date();
    const diffMs = now - date;
    const diffMins = Math.floor(diffMs / 60000);
    const diffHours = Math.floor(diffMins / 60);
    const diffDays = Math.floor(diffHours / 24);
    
    if (diffMins < 1) return "just now";
    if (diffMins < 60) return `${diffMins}m ago`;
    if (diffHours < 24) return `${diffHours}h ago`;
    if (diffDays < 7) return `${diffDays}d ago`;
    
    return null; // Return null to signal "use full date instead"
  } catch (error) {
    return null;
  }
};

/**
 * Get timezone label from value
 * @param {string} tzValue - IANA timezone string
 * @returns {string} Human-readable label
 */
export const getTimezoneLabel = (tzValue) => {
  const tz = TIMEZONES.find(t => t.value === tzValue);
  return tz ? tz.label : tzValue;
};
